import React from 'react';
import { NotificationBell } from './NotificationBell';
import { useBreadcrumbSlot } from './useBreadcrumbSlot';

export interface AppTopBarProps {
  /** Left side — e.g. a breadcrumb (Figma "🏠 Home"). */
  headerLeft?: React.ReactNode;
  /** Optional action shown to the LEFT of the bell (e.g. the "Create Campaign"
   *  button on campaigns, "Draft saved" chip on the wizard). The bell always
   *  stays pinned to the extreme-right corner. */
  headerRight?: React.ReactNode;
  /** Campaign-detail route base for notification deep links. */
  campaignRouteBase?: string;
}

/**
 * Top app bar (Figma "App Bar"): off-white, 52px, sticky. Breadcrumb/slot at the
 * far left; the optional action then the notification bell pinned to the extreme
 * right corner. The bell is ALWAYS the rightmost element; dynamic actions
 * (Create Campaign / Draft saved) sit to its left. Full-width (not capped) so the
 * bell always sits in the corner.
 */
export function AppTopBar({ headerLeft, headerRight, campaignRouteBase }: AppTopBarProps) {
  // A page may publish a richer breadcrumb (campaign name › tab › sub) into the
  // slot; render it when present, else fall back to the route's headerLeft.
  const breadcrumbSlot = useBreadcrumbSlot((s) => s.node);
  return (
    <header className="sticky top-0 z-30 h-13 shrink-0 border-b border-border bg-white">
      <div className="flex size-full items-center justify-between px-4">
        {/* Clips instead of growing, so the breadcrumb can't widen the page or run under the actions.
            The -m-1/p-1 pair gives focus rings room inside the clip. */}
        <div className="-m-1 flex min-w-0 flex-1 items-center overflow-hidden p-1">{breadcrumbSlot ?? headerLeft}</div>
        <div className="flex shrink-0 items-center gap-3 pl-3">
          {headerRight}
          <NotificationBell campaignRouteBase={campaignRouteBase} />
        </div>
      </div>
    </header>
  );
}
